import { Link } from "react-router-dom";
import { LINKS, BRAND, ROUTES, SOCIAL } from "@/config/links";
import { useLang } from "@/i18n";
import { trackCta, capture, getPageSlug, captureWhatsAppClickEvent } from "@/lib/analytics";

const LinkedInIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.6v1.7h.1c.5-.9 1.7-1.9 3.6-1.9 3.9 0 4.7 2.6 4.7 5.9V21h-4v-5.5c0-1.3 0-3-1.9-3s-2.1 1.4-2.1 2.9V21h-4V9Z" />
  </svg>
);
const InstagramIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...p}>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);
const FacebookIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
    <path d="M13 22v-8h2.7l.4-3.2H13V8.7c0-.9.3-1.5 1.6-1.5H16V4.3c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4v2.6H7V14h2.6v8H13Z" />
  </svg>
);

export const Footer = () => {
  const { t, l } = useLang();
  const year = new Date().getFullYear();

  const sitemap = [
    {
      heading: t("footer.services"),
      links: [
        { label: t("footer.svc_executive_coaching"), to: ROUTES.executiveCoaching },
        { label: t("footer.svc_high_stakes"), to: ROUTES.highStakesPresentationCoaching },
        { label: t("footer.svc_epd"), to: ROUTES.executivePresenceDevelopment },
        { label: t("footer.svc_corporate"), to: ROUTES.corporateTraining },
        { label: t("footer.svc_ld"), to: ROUTES.ldPartnerships },
      ],
    },
    {
      heading: t("footer.insights"),
      links: [
        { label: t("footer.ins_diagnostic"), to: ROUTES.nextPresentationReadinessCheck, ctaId: "footer-audit-link", targetAssetId: "next_presentation_readiness" },
        { label: t("footer.ins_articles"), to: ROUTES.articles },
        { label: t("footer.ins_case_studies"), to: ROUTES.caseStudies },
        { label: t("footer.ins_guides"), to: ROUTES.guides },
        { label: t("footer.ins_tools"), to: ROUTES.tools },
      ],
    },
    {
      heading: t("footer.company"),
      links: [
        { label: t("footer.co_about"), to: ROUTES.about },
        { label: t("footer.co_booking"), to: ROUTES.booking, ctaId: "footer-booking-link", targetService: "general_booking" },
        { label: t("footer.co_contact"), to: ROUTES.contact, ctaId: "footer-contact-link", targetAssetId: "contact_request" },
        { label: t("footer.co_privacy"), to: ROUTES.privacy },
        { label: t("footer.co_terms"), to: ROUTES.terms },
      ],
    },
  ];

  return (
    <footer
      className="relative z-10 bg-paper border-t border-ink/10 py-16"
      data-testid="site-footer"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-4">
            <Link to={l("/")} className="inline-flex flex-col leading-none">
              <span className="font-serif text-2xl md:text-3xl text-ink">{BRAND.company}</span>
              <span className="font-hand text-terracotta text-lg -mt-1">
                {t("brand.tagline_hand")}
              </span>
            </Link>
            <p className="mt-5 font-body text-ink-soft max-w-sm leading-relaxed">
              {t("footer.tagline_p", { year: BRAND.foundedYear })}
            </p>

            <div className="mt-7 flex items-center gap-4" data-testid="footer-social">
              <a
                href={SOCIAL.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
                className="w-10 h-10 border border-ink/25 flex items-center justify-center text-ink hover:text-mustard hover:border-mustard transition-colors"
              >
                <LinkedInIcon width={18} height={18} />
              </a>
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram"
                className="w-10 h-10 border border-ink/25 flex items-center justify-center text-ink hover:text-mustard hover:border-mustard transition-colors"
              >
                <InstagramIcon width={18} height={18} />
              </a>
              <a
                href={SOCIAL.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="footer-facebook"
                className="w-10 h-10 border border-ink/25 flex items-center justify-center text-ink hover:text-mustard hover:border-mustard transition-colors"
              >
                <FacebookIcon width={18} height={18} />
              </a>
            </div>
          </div>

          {sitemap.map((col) => (
            <div key={col.heading} className="md:col-auto lg:col-span-2">
              <p className="font-body text-xs uppercase tracking-[0.22em] text-pencil mb-4">
                {col.heading}
              </p>
              <ul className="space-y-3 font-body text-ink-soft">
                {col.links.map((li) => (
                  <li key={li.to}>
                    <Link
                      to={l(li.to)}
                      onClick={
                        li.ctaId
                          ? () => trackCta({ ctaId: li.ctaId, location: "footer", href: li.to, targetAssetId: li.targetAssetId, targetService: li.targetService })
                          : undefined
                      }
                      className="hover:text-terracotta transition-colors"
                    >
                      {li.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="md:col-span-2">
            <p className="font-body text-xs uppercase tracking-[0.22em] text-pencil mb-4">
              {t("footer.direct")}
            </p>
            <a
              href={LINKS.email}
              className="font-body text-ink-soft hover:text-terracotta transition-colors break-words"
              data-testid="footer-email"
            >
              {BRAND.emailAddress}
            </a>
            <div className="mt-4">
              <a
                href={SOCIAL.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-ink-soft hover:text-terracotta transition-colors"
                data-testid="footer-whatsapp"
                onClick={() => {
                  trackCta({ ctaId: "footer-whatsapp", location: "footer", href: SOCIAL.whatsapp });
                  capture("whatsapp_click", { cta_location: "footer", page_path: window.location.pathname, page_slug: getPageSlug() });
                  // ChatGPT Ads custom conversion events, pass 2 (2026-02).
                  captureWhatsAppClickEvent();
                }}
              >
                {t("footer.whatsapp")}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-ink/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="font-body text-sm text-pencil">
            {t("footer.copyright", { year, company: BRAND.company })}
          </p>
          <p className="font-hand text-lg text-pencil">{t("brand.tagline_short")}</p>
        </div>
      </div>
    </footer>
  );
};
